import { useEffect, useState } from "react";
import type { CSSProperties, KeyboardEvent, PointerEvent, ReactNode } from "react";
import clsx from "clsx";
import { DockHeightContext } from "./dockContext";
import {
  AXIS_CONFIG,
  clampDockSize,
  defaultDockSize,
  DOCK_SHEET_BREAKPOINT,
  dockLimits,
  FULL_DOCK_HEIGHT,
  nextDockSizeForKey,
  readDockSize,
  writeDockSize,
} from "./dockSize";
import type { DockAxis } from "./dockSize";
import styles from "./AssistantDock.module.scss";

interface AssistantDockProps {
  children: ReactNode;
}

const AXES: DockAxis[] = ["width", "height", "sideHeight"];
function useViewport() {
  const [viewport, setViewport] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));
  useEffect(() => {
    const update = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return viewport;
}

function headerHeight(): number {
  const value = getComputedStyle(document.documentElement).getPropertyValue("--header-height");
  return Number.parseFloat(value) || 0;
}

export function AssistantDock({ children }: AssistantDockProps) {
  const viewport = useViewport();
  const isSheet = viewport.width < DOCK_SHEET_BREAKPOINT;
  const heightAxis: DockAxis = isSheet ? "height" : "sideHeight";
  const available: Record<DockAxis, number> = {
    width: viewport.width,
    height: viewport.height,
    sideHeight: viewport.height - headerHeight(),
  };
  const [stored, setStored] = useState<Record<DockAxis, number | null>>(
    () =>
      Object.fromEntries(AXES.map((axis) => [axis, readDockSize(axis)])) as Record<
        DockAxis,
        number | null
      >,
  );
  const [draggingAxis, setDraggingAxis] = useState<DockAxis | null>(null);

  const axisState = (axis: DockAxis) => {
    const limits = dockLimits(axis, available[axis]);
    const size = clampDockSize(stored[axis] ?? defaultDockSize(axis, available[axis]), limits);
    return { axis, limits, size };
  };
  const resize = (axis: DockAxis, next: number) => {
    const { limits } = axisState(axis);
    const clamped = clampDockSize(next, limits);
    const kept = axis === "sideHeight" && clamped >= limits.max ? FULL_DOCK_HEIGHT : clamped;
    writeDockSize(axis, kept);
    setStored((current) => ({ ...current, [axis]: kept }));
  };

  const width = axisState("width");
  const height = axisState(heightAxis);
  const isFloating = !isSheet && height.size < height.limits.max;

  const renderHandle = (state: ReturnType<typeof axisState>, className: string) => {
    const { axis, limits, size } = state;
    const { label, orientation } = AXIS_CONFIG[axis];
    const isHorizontal = orientation === "horizontal";

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      const next = nextDockSizeForKey(event.key, axis, size, limits, event.shiftKey);
      if (next === null) return;
      event.preventDefault();
      resize(axis, next);
    };

    const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      const origin = isHorizontal ? event.clientY : event.clientX;
      const target = event.currentTarget;
      target.setPointerCapture?.(event.pointerId);
      setDraggingAxis(axis);

      const move = (moveEvent: globalThis.PointerEvent) => {
        const position = isHorizontal ? moveEvent.clientY : moveEvent.clientX;
        resize(axis, size + (origin - position));
      };
      const end = () => {
        setDraggingAxis(null);
        target.removeEventListener("pointermove", move);
        target.removeEventListener("pointerup", end);
        target.removeEventListener("pointercancel", end);
      };
      target.addEventListener("pointermove", move);
      target.addEventListener("pointerup", end);
      target.addEventListener("pointercancel", end);
    };

    return (
      <div
        role="separator"
        aria-label={label}
        aria-orientation={orientation}
        aria-valuenow={size}
        aria-valuemin={limits.min}
        aria-valuemax={limits.max}
        tabIndex={0}
        className={clsx(styles.handle, className, draggingAxis === axis && styles.dragging)}
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
      />
    );
  };

  return (
    <>
      {isSheet && <div className={styles.spacer} style={{ height: height.size }} aria-hidden />}
      <div
        className={clsx(
          styles.dock,
          isSheet ? styles.sheet : styles.side,
          isFloating && styles.floating,
        )}
        style={
          {
            "--dock-width": `${width.size}px`,
            "--dock-height": `${height.size}px`,
          } as CSSProperties
        }
      >
        {isSheet ? (
          renderHandle(height, styles.sheetHandle)
        ) : (
          <>
            {renderHandle(width, styles.widthHandle)}
            {renderHandle(height, styles.topHandle)}
          </>
        )}
        <DockHeightContext.Provider value={height.size}>{children}</DockHeightContext.Provider>
      </div>
    </>
  );
}
